// CsvImport — bulk SKU cost update from a vendor CSV.
//
// Flow:
//   1. User clicks "Upload vendor prices" on the Inventory page
//   2. File picker opens, user selects a CSV
//   3. Client-side parsing: expects columns `code` and `cost` (dollars,
//      NOT cents — vendor sheets use dollars). Column matching is
//      case-insensitive and ignores extra columns.
//   4. Preview table: each row shows SKU code → matched SKU name →
//      current cost → new cost → delta. Rows that don't match any SKU
//      are highlighted as unmatched (they'll be skipped).
//   5. User clicks "Apply X changes"
//   6. Frontend fires N parallel PATCH /api/skus/:id requests
//      (one per matched SKU) to update costCents
//   7. On success, refetch the inventory list so the new costs land
//
// Owner-only because it touches cost_cents.
//
// Renders bare content (no outer Card) — designed to live inside a
// Dialog launched from the Inventory page.
//
// CSV format expected:
//   code,cost
//   5IN-COIL-WHT,1.25
//   2X3-DS-WHT,8.75
//
// Also accepts: sku_code, sku, item_code as aliases for `code`.
// Also accepts: cost_cents, unit_cost, price as aliases for `cost`.
// If `cost_cents` is detected, values are treated as integer cents
// (not dollars).

import { useState, useMemo, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { getInventorySkus } from '../api/client.js';
import type { InventorySkuRow } from '@gutterinv/shared';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { cn } from '../lib/utils.js';
import { formatCents } from '../lib/format.js';

interface ParsedRow {
  code: string;
  newCostCents: number;
  matchedSku: InventorySkuRow | null;
}

const CODE_ALIASES = new Set(['code', 'sku_code', 'sku', 'item_code', 'item']);
const COST_ALIASES = new Set(['cost', 'unit_cost', 'price']);
const COST_CENTS_ALIASES = new Set(['cost_cents', 'unit_cost_cents']);

export function CsvImport() {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [parsed, setParsed] = useState<ParsedRow[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<{
    success: number;
    failed: number;
  } | null>(null);

  const { data: inventoryData } = useQuery({
    queryKey: ['inventory', 'skus'],
    queryFn: () => getInventorySkus(),
  });
  const skus = inventoryData?.skus ?? [];
  const skuByCode = useMemo(
    () => new Map(skus.map((s) => [s.code.toLowerCase(), s])),
    [skus],
  );

  const matchedRows = useMemo(
    () => (parsed ?? []).filter((r) => r.matchedSku !== null),
    [parsed],
  );
  const unmatchedRows = useMemo(
    () => (parsed ?? []).filter((r) => r.matchedSku === null),
    [parsed],
  );
  const changedRows = useMemo(
    () =>
      matchedRows.filter(
        (r) => r.matchedSku !== null && r.newCostCents !== r.matchedSku.costCents,
      ),
    [matchedRows],
  );

  const handleFile = useCallback(
    (file: File) => {
      setApplyResult(null);
      setParseError(null);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const rows = parseCsv(text, skuByCode);
          if (rows.length === 0) {
            setParseError('No data rows found in the CSV.');
            setParsed(null);
            return;
          }
          setParsed(rows);
        } catch (err) {
          setParseError(
            err instanceof Error ? err.message : 'Failed to parse CSV',
          );
          setParsed(null);
        }
      };
      reader.readAsText(file);
    },
    [skuByCode],
  );

  const handleApply = async () => {
    if (changedRows.length === 0) return;
    setApplying(true);
    setApplyResult(null);

    let success = 0;
    let failed = 0;

    // Fire all patches in parallel — small number of SKUs, quick round trips
    await Promise.all(
      changedRows.map(async (row) => {
        try {
          const res = await fetch(`/api/skus/${row.matchedSku!.skuId}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ costCents: row.newCostCents }),
          });
          if (res.ok) success++;
          else failed++;
        } catch {
          failed++;
        }
      }),
    );

    setApplying(false);
    setApplyResult({ success, failed });

    if (success > 0) {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['sku-categories'] });
    }
  };

  const reset = () => {
    setParsed(null);
    setParseError(null);
    setApplyResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Upload a CSV with columns <code className="rounded bg-secondary px-1 text-xs">code</code> and{' '}
        <code className="rounded bg-secondary px-1 text-xs">cost</code> (dollars per unit).
        Matched SKUs will have their cost basis updated.
      </p>

        {/* File picker */}
        <div className="flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.tsv,.txt"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
            }}
            className="text-sm file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-foreground hover:file:bg-accent"
          />
          {parsed && (
            <Button size="sm" variant="ghost" onClick={reset}>
              <X className="h-3.5 w-3.5" />
              Clear
            </Button>
          )}
        </div>

        {/* Parse error */}
        {parseError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {parseError}
          </div>
        )}

        {/* Apply result */}
        {applyResult && (
          <div
            className={cn(
              'rounded-md border px-3 py-2 text-xs',
              applyResult.failed > 0
                ? 'border-warning/40 bg-warning/10 text-warning-foreground'
                : 'border-primary/40 bg-primary/10 text-primary',
            )}
          >
            {applyResult.success > 0 && (
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {applyResult.success} SKU{applyResult.success === 1 ? '' : 's'} updated.
              </span>
            )}
            {applyResult.failed > 0 && (
              <span>
                {applyResult.failed} failed — check the console or retry.
              </span>
            )}
          </div>
        )}

        {/* Preview table */}
        {parsed && parsed.length > 0 && (
          <>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50">
                  <tr className="border-b border-border">
                    <th className="px-3 py-2 text-left">
                      <span className="eyebrow">Code</span>
                    </th>
                    <th className="px-3 py-2 text-left">
                      <span className="eyebrow">SKU</span>
                    </th>
                    <th className="px-3 py-2 text-right">
                      <span className="eyebrow">Current</span>
                    </th>
                    <th className="px-3 py-2 text-right">
                      <span className="eyebrow">New</span>
                    </th>
                    <th className="px-3 py-2 text-right">
                      <span className="eyebrow">Delta</span>
                    </th>
                    <th className="px-3 py-2 text-left">
                      <span className="eyebrow">Status</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.map((row, i) => {
                    const matched = row.matchedSku !== null;
                    const currentCents = row.matchedSku?.costCents ?? 0;
                    const delta = row.newCostCents - currentCents;
                    const changed = matched && delta !== 0;
                    return (
                      <tr
                        key={i}
                        className={cn(
                          'border-b border-border last:border-b-0',
                          !matched && 'bg-destructive/5',
                        )}
                      >
                        <td className="px-3 py-2 font-mono text-xs">{row.code}</td>
                        <td className="px-3 py-2 text-foreground">
                          {matched ? row.matchedSku!.name : '—'}
                        </td>
                        <td className="tnum px-3 py-2 text-right text-muted-foreground">
                          {matched ? formatCents(currentCents) : '—'}
                        </td>
                        <td className="tnum px-3 py-2 text-right font-semibold text-foreground">
                          {formatCents(row.newCostCents)}
                        </td>
                        <td
                          className={cn(
                            'tnum px-3 py-2 text-right',
                            delta > 0
                              ? 'text-destructive'
                              : delta < 0
                                ? 'text-primary'
                                : 'text-taupe',
                          )}
                        >
                          {matched
                            ? delta === 0
                              ? '—'
                              : `${delta > 0 ? '+' : ''}${formatCents(delta)}`
                            : '—'}
                        </td>
                        <td className="px-3 py-2">
                          {!matched ? (
                            <Badge variant="destructive">Unmatched</Badge>
                          ) : changed ? (
                            <Badge variant="warning">Changed</Badge>
                          ) : (
                            <Badge variant="outline">Same</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Summary + apply */}
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="text-muted-foreground">
                {parsed.length} rows · {matchedRows.length} matched ·{' '}
                {changedRows.length} changed · {unmatchedRows.length} unmatched
              </span>
              {unmatchedRows.length > 0 && (
                <span className="flex items-center gap-1 text-xs text-warning">
                  <AlertTriangle className="h-3 w-3" />
                  Unmatched rows will be skipped
                </span>
              )}
              <div className="flex-1" />
              <Button
                onClick={handleApply}
                disabled={changedRows.length === 0 || applying}
              >
                {applying
                  ? 'Applying…'
                  : `Apply ${changedRows.length} change${changedRows.length === 1 ? '' : 's'}`}
              </Button>
            </div>
          </>
        )}
    </div>
  );
}

// ============================================================
// CSV PARSER
// ============================================================

function parseCsv(
  text: string,
  skuByCode: Map<string, InventorySkuRow>,
): ParsedRow[] {
  // Detect separator: if any line has tabs, treat as TSV; otherwise comma
  const sep = text.includes('\t') ? '\t' : ',';
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) {
    throw new Error('CSV must have a header row and at least one data row.');
  }

  // Parse header
  const headers = lines[0]!.split(sep).map((h) => h.trim().toLowerCase().replace(/^["']|["']$/g, ''));
  let codeIdx = headers.findIndex((h) => CODE_ALIASES.has(h));
  let costIdx = headers.findIndex((h) => COST_ALIASES.has(h));
  let costCentsIdx = headers.findIndex((h) => COST_CENTS_ALIASES.has(h));

  if (codeIdx === -1) {
    throw new Error(
      `Missing "code" column. Expected one of: ${[...CODE_ALIASES].join(', ')}. ` +
        `Found: ${headers.join(', ')}`,
    );
  }

  const isCents = costCentsIdx !== -1;
  const valueIdx = isCents ? costCentsIdx : costIdx;
  if (valueIdx === -1) {
    throw new Error(
      `Missing "cost" column. Expected one of: ${[...COST_ALIASES, ...COST_CENTS_ALIASES].join(', ')}. ` +
        `Found: ${headers.join(', ')}`,
    );
  }

  // Parse data rows
  const rows: ParsedRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i]!.split(sep).map((c) => c.trim().replace(/^["']|["']$/g, ''));
    const code = cols[codeIdx] ?? '';
    const valueStr = (cols[valueIdx] ?? '').replace(/[$,]/g, '');
    if (!code || !valueStr) continue;

    const value = Number(valueStr);
    if (Number.isNaN(value) || value < 0) continue;

    const newCostCents = isCents ? Math.round(value) : Math.round(value * 100);
    const matchedSku = skuByCode.get(code.toLowerCase()) ?? null;

    rows.push({ code, newCostCents, matchedSku });
  }

  return rows;
}
